/**
 * Screenshot markup — the marks a reporter draws on a screenshot before submitting an
 * Issue, and the pure geometry behind drawing, selecting and moving them.
 *
 * Every coordinate is in the IMAGE's natural pixels, never the screen's, so a mark drawn
 * on a phone lands in the same place on the full-size export. Line thickness and text
 * size are derived from the image's size (`markStyle`), not the device it was drawn on,
 * so text stays readable at the image's real size.
 *
 * Marks stay editable until the Issue is submitted; only then are they burned into the
 * pixels (src/lib/markup/render.ts).
 */

export const MARK_COLORS = ["red", "yellow", "blue", "green"] as const;
export type MarkColor = (typeof MARK_COLORS)[number];
export const DEFAULT_MARK_COLOR: MarkColor = "red";

/** The design token behind each pen (design-system/tokens.css). */
export const MARK_COLOR_TOKENS: Record<MarkColor, string> = {
  red: "--markup-red",
  yellow: "--markup-yellow",
  blue: "--markup-blue",
  green: "--markup-green",
};

export const MARK_COLOR_LABELS: Record<MarkColor, string> = {
  red: "Red",
  yellow: "Yellow",
  blue: "Blue",
  green: "Green",
};

export type MarkTool = "select" | "box" | "arrow" | "text" | "redact";

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Mark =
  | ({ id: string; kind: "box"; color: MarkColor } & Rect)
  | ({ id: string; kind: "redact" } & Rect)
  | { id: string; kind: "arrow"; color: MarkColor; x1: number; y1: number; x2: number; y2: number }
  | { id: string; kind: "text"; color: MarkColor; x: number; y: number; text: string };

export interface MarkStyle {
  strokeWidth: number;
  fontSize: number;
  arrowHeadLength: number;
  /** How close (image px) a press must be to a line to pick it. */
  hitTolerance: number;
}

/** Thickness and text size for an image, from its longest edge. */
export function markStyle(imageWidth: number, imageHeight: number): MarkStyle {
  const longEdge = Math.max(1, imageWidth, imageHeight);
  const strokeWidth = Math.max(3, Math.round(longEdge / 400));
  const fontSize = Math.max(16, Math.round(longEdge / 55));
  return {
    strokeWidth,
    fontSize,
    arrowHeadLength: Math.max(14, strokeWidth * 5),
    hitTolerance: Math.max(8, strokeWidth * 3),
  };
}

/** The rectangle two drag corners span, whichever way the drag went. */
export function normalizeRect(a: Point, b: Point): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

export function clampPoint(p: Point, width: number, height: number): Point {
  return { x: Math.min(Math.max(p.x, 0), width), y: Math.min(Math.max(p.y, 0), height) };
}

/** The arrowhead's three points: the tip (at the release point) and the two wings. */
export function arrowHead(
  from: Point,
  to: Point,
  length: number,
): { tip: Point; left: Point; right: Point } {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const spread = Math.PI / 7;
  return {
    tip: to,
    left: {
      x: to.x - length * Math.cos(angle - spread),
      y: to.y - length * Math.sin(angle - spread),
    },
    right: {
      x: to.x - length * Math.cos(angle + spread),
      y: to.y - length * Math.sin(angle + spread),
    },
  };
}

/** Approximate box a text mark occupies (top-left at its anchor). */
export function textBounds(mark: { x: number; y: number; text: string }, fontSize: number): Rect {
  const lines = mark.text.split("\n");
  const longest = Math.max(1, ...lines.map((l) => l.length));
  return { x: mark.x, y: mark.y, w: longest * fontSize * 0.62, h: lines.length * fontSize * 1.25 };
}

export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function inRect(p: Point, r: Rect, pad: number): boolean {
  return p.x >= r.x - pad && p.x <= r.x + r.w + pad && p.y >= r.y - pad && p.y <= r.y + r.h + pad;
}

function nearRectEdge(p: Point, r: Rect, tolerance: number): boolean {
  if (!inRect(p, r, tolerance)) return false;
  const inside =
    p.x > r.x + tolerance &&
    p.x < r.x + r.w - tolerance &&
    p.y > r.y + tolerance &&
    p.y < r.y + r.h - tolerance;
  return !inside;
}

/** Does a press at `p` pick this mark? Box: near its outline. Redact: anywhere inside. */
export function hitsMark(mark: Mark, p: Point, style: MarkStyle): boolean {
  switch (mark.kind) {
    case "box":
      return nearRectEdge(p, mark, style.hitTolerance);
    case "redact":
      return inRect(p, mark, style.hitTolerance / 2);
    case "arrow":
      return (
        distanceToSegment(p, { x: mark.x1, y: mark.y1 }, { x: mark.x2, y: mark.y2 }) <=
        style.hitTolerance
      );
    case "text":
      return inRect(p, textBounds(mark, style.fontSize), style.hitTolerance / 2);
  }
}

/** The topmost mark under `p` (marks later in the list are drawn on top). */
export function markAt(marks: readonly Mark[], p: Point, style: MarkStyle): Mark | null {
  for (let i = marks.length - 1; i >= 0; i--) {
    const mark = marks[i];
    if (mark && hitsMark(mark, p, style)) return mark;
  }
  return null;
}

export function translateMark(mark: Mark, dx: number, dy: number): Mark {
  switch (mark.kind) {
    case "box":
    case "redact":
    case "text":
      return { ...mark, x: mark.x + dx, y: mark.y + dy };
    case "arrow":
      return { ...mark, x1: mark.x1 + dx, y1: mark.y1 + dy, x2: mark.x2 + dx, y2: mark.y2 + dy };
  }
}

/** Too small to have been meant — a click, not a drag. Dropped instead of committed. */
export function isMeaningful(mark: Mark, style: MarkStyle): boolean {
  const min = style.strokeWidth * 2;
  switch (mark.kind) {
    case "box":
    case "redact":
      return mark.w >= min && mark.h >= min;
    case "arrow":
      return Math.hypot(mark.x2 - mark.x1, mark.y2 - mark.y1) >= style.arrowHeadLength;
    case "text":
      return mark.text.trim().length > 0;
  }
}

export function recolorMark(mark: Mark, color: MarkColor): Mark {
  return mark.kind === "redact" ? mark : { ...mark, color };
}

export function hasRedaction(marks: readonly Mark[]): boolean {
  return marks.some((m) => m.kind === "redact");
}

/** The text notes, in the order they were placed — shown beside the image and sent along. */
export function textNotes(marks: readonly Mark[]): string[] {
  return marks
    .filter((m): m is Extract<Mark, { kind: "text" }> => m.kind === "text")
    .map((m) => m.text.trim())
    .filter((t) => t.length > 0);
}

let counter = 0;
export function newMarkId(): string {
  counter += 1;
  return `m${Date.now().toString(36)}${counter.toString(36)}`;
}
